import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { apiClient } from '../../config/api';

interface NutrientTrendsChartProps {
  user: any;
  clientMode?: boolean;
}

export const NutrientTrendsChart: React.FC<NutrientTrendsChartProps> = ({ user }) => {
  const [selectedPeriod, setSelectedPeriod] = useState<'7d' | '30d' | '90d'>('30d');
  const [selectedNutrients, setSelectedNutrients] = useState<string[]>(['calories', 'protein', 'vitamin_d']);

  // Fetch nutrition data for different time periods to create trends
  const { data: trendsData, isLoading } = useQuery({
    queryKey: ['nutrition-trends', user?.id, selectedPeriod],
    queryFn: () => apiClient.getNutritionTrends(user?.id, selectedPeriod),
    enabled: !!user?.id,
  });

  const nutrientOptions = [
    { value: 'calories', label: 'Calories', color: '#8884d8', rda: null, unit: 'kcal' },
    { value: 'protein', label: 'Protein', color: '#82ca9d', rda: 50, unit: 'g' },
    { value: 'carbs', label: 'Carbohydrates', color: '#ffc658', rda: null, unit: 'g' },
    { value: 'fat', label: 'Fat', color: '#ff7300', rda: null, unit: 'g' },
    { value: 'fiber', label: 'Fibre', color: '#00ff00', rda: 25, unit: 'g' },
    { value: 'vitamin_d', label: 'Vitamin D', color: '#0088fe', rda: 10, unit: 'μg' },
    { value: 'vitamin_b12', label: 'Vitamin B12', color: '#ff0088', rda: 2.4, unit: 'μg' },
    { value: 'vitamin_c', label: 'Vitamin C', color: '#88ff00', rda: 40, unit: 'mg' },
    { value: 'calcium', label: 'Calcium', color: '#8800ff', rda: 700, unit: 'mg' },
    { value: 'iron', label: 'Iron', color: '#ff8800', rda: 14.8, unit: 'mg' }
  ];

  const toggleNutrient = (nutrient: string) => {
    setSelectedNutrients(prev => 
      prev.includes(nutrient) 
        ? prev.filter(n => n !== nutrient)
        : [...prev, nutrient].slice(0, 5) // Limit to 5 lines for readability
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric'
    });
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-300 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900">{formatDate(label)}</p>
          {payload.map((entry: any, index: number) => {
            const nutrient = nutrientOptions.find(n => n.value === entry.dataKey);
            return (
              <p key={index} style={{ color: entry.color }} className="text-sm">
                {nutrient?.label}: {entry.value?.toFixed(1)} {nutrient?.unit}
                {nutrient?.rda && (
                  <span className="text-gray-500 ml-1">
                    ({((entry.value / nutrient.rda) * 100).toFixed(0)}% RDA)
                  </span>
                )}
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  if (isLoading) {
    return (
      <div className="card">
        <div className="card-body">
          <div className="bg-gray-200 rounded-lg h-96 animate-pulse" />
        </div>
      </div>
    );
  }

  if (!trendsData?.daily_averages || trendsData.daily_averages.length === 0) {
    return (
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">Nutrient Trends Over Time</h3>
        </div>
        <div className="card-body">
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📈</div>
            <h4 className="text-xl font-medium text-gray-900 mb-2">Insufficient Data</h4>
            <p className="text-gray-500 mb-4">
              Need at least 3 days of meal data to generate trend charts.
            </p>
            <p className="text-sm text-gray-400">
              Keep logging meals to see your nutrition patterns over time!
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Nutrient Trends Over Time</h3>
            <p className="text-sm text-gray-500 mt-1">
              Track nutrition changes and patterns ({trendsData.daily_averages.length} days of data)
            </p>
          </div>
          
          <div className="flex gap-2 mt-4 sm:mt-0">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value as '7d' | '30d' | '90d')}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm"
            >
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
            </select>
          </div>
        </div>
      </div>

      <div className="card-body">
        {/* Nutrient Selector */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-900 mb-3">
            Select Nutrients to Track (max 5):
          </h4>
          <div className="flex flex-wrap gap-2">
            {nutrientOptions.map((nutrient) => (
              <button
                key={nutrient.value}
                onClick={() => toggleNutrient(nutrient.value)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  selectedNutrients.includes(nutrient.value)
                    ? 'text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                style={{
                  backgroundColor: selectedNutrients.includes(nutrient.value) 
                    ? nutrient.color 
                    : undefined
                }}
              >
                {nutrient.label}
                {nutrient.rda && (
                  <span className="ml-1 text-xs opacity-75">
                    RDA: {nutrient.rda}{nutrient.unit}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Chart */}
        <div style={{ width: '100%', height: '400px' }}>
          <ResponsiveContainer>
            <LineChart data={trendsData.daily_averages}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tickFormatter={formatDate}
                interval="preserveStartEnd"
              />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              
              {selectedNutrients.map((nutrientKey) => {
                const nutrient = nutrientOptions.find(n => n.value === nutrientKey);
                if (!nutrient) return null;
                
                return (
                  <React.Fragment key={nutrientKey}>
                    <Line
                      type="monotone"
                      dataKey={nutrientKey}
                      stroke={nutrient.color}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      name={`${nutrient.label} (${nutrient.unit})`}
                    />
                    {/* Add RDA reference line if available */}
                    {nutrient.rda && (
                      <ReferenceLine 
                        y={nutrient.rda} 
                        stroke={nutrient.color} 
                        strokeDasharray="5 5"
                        strokeOpacity={0.5}
                        label={`${nutrient.label} RDA`}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Summary Stats */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
          <div className="text-center">
            <div className="text-lg font-bold text-blue-600">
              {trendsData.daily_averages.length}
            </div>
            <div className="text-sm text-gray-600">Days Tracked</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-green-600">
              {selectedPeriod}
            </div>
            <div className="text-sm text-gray-600">Time Period</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-purple-600">
              {selectedNutrients.length}
            </div>
            <div className="text-sm text-gray-600">Nutrients Shown</div>
          </div>
        </div>

        {/* Tips */}
        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
          <h5 className="font-medium text-blue-900 mb-2">💡 Trend Analysis Tips:</h5>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Look for consistent patterns - are weekends different from weekdays?</li>
            <li>• Watch for gradual improvements or declines over time</li>
            <li>• Compare actual values to RDA lines (dashed lines)</li>
            <li>• Use longer time periods (30-90 days) for better trend visibility</li>
          </ul>
        </div>
      </div>
    </div>
  );
};